import React, { useRef, useCallback, useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  ConfigProvider,
  Image,
  Card,
  Radio,
  Modal,
  Form,
  Input,
  Carousel,
  Divider,
} from "antd";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";
import {
  LoadingOutlined,
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { setCanFetchNotifProgram } from "../redux/features/main/onboardingSlice";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import defaultImage from "../assets/images/png/general/img_thumb_default.png";
import onboarding1 from "../assets/images/png/general/img_onboarding1_general.png";
import onboarding2 from "../assets/images/png/general/img_onboarding2_general.png";
import onboarding3 from "../assets/images/png/general/img_onboarding3_general.png";

// for translate
import { useTranslation } from "react-i18next";

import { getAppName } from "../utils";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";

import {
  iconGeneralBackground,
  colorTextDesc,
  ColorPrimary,
  backgroundCard,
  ColorSecondary,
  colorTextTitle,
  colorTextTitleMobile,
} from "../config/constant/color/index";

const ModalOnboarding = () => {
  const { t, i18n } = useTranslation();
  const lastLogin = useSelector((state) => state.onboarding.lastLogin);
  const dispatch = useDispatch();

  const sliderRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const screens = useBreakpoint();
  const isScallingVersion =
    (window.innerWidth <= 991 && window.innerWidth >= 768) ||
    window.innerWidth === 581;
  const isMobileVersion = screens.xs;

  var btnPrev = document.querySelector("#id-btn-prev-onboarding");
  var btnNext = document.querySelector("#id-btn-next-onboarding");

  const onboardingData = [
    {
      image: onboarding1,
      title: `${t("feature.onboarding.welcome_message")}${getAppName()}`,
      desc: t("feature.onboarding.intro_message"),
    },
    {
      image: onboarding2,
      title: t("feature.onboarding.elevate_competency"),
      desc: t("feature.onboarding.competency_description"),
    },
    {
      image: onboarding3,
      title: t("feature.onboarding.build_portfolio"),
      desc: t("feature.onboarding.portfolio_description"),
    },
  ];

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slideNext();
  }, []);

  const onSwiperMove = (e) => {
    if (btnPrev !== null && btnNext !== null) {
      if (e.activeIndex === 0) {
        btnPrev.classList.add("hidden");
      } else {
        btnPrev.classList.remove("hidden");
      }

      if (e.activeIndex === 2) {
        btnNext.classList.add("hidden");
      } else {
        btnNext.classList.remove("hidden");
      }
    }
  };

  useEffect(() => {
    if (lastLogin !== null) {
      if (
        lastLogin.toLowerCase() !== "web" &&
        lastLogin.toLowerCase() !== "mobile_and_web" &&
        lastLogin !== null
      ) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    } else {
      dispatch(setCanFetchNotifProgram(true));
    }
  }, []);

  return (
    <ConfigProvider
      theme={{
        components: {
          Carousel: {
            dotActiveWidth: 12,
            dotHeight: 12,
            dotWidth: 12,
          },
        },
        token: {
          fontFamily: "Roboto",
          borderRadiusLG: isMobileVersion ? 12 : 16,
        },
      }}
    >
      <Modal
        className="modal-onboarding"
        open={isOpen}
        maskClosable={false}
        closable={false}
        width={
          isMobileVersion ? (isScallingVersion ? `${585 * 0.9}px` : "90%") : 480
        }
        transitionName="ant-fade"
        keyboard
        centered
        footer={false}
        title={false}
        style={{ borderRadius: isMobileVersion ? 12 : 16 }}
      >
        <div
          style={{
            padding: isMobileVersion ? "18px" : "32px 32px 24px 32px",
            position: "relative",
          }}
        >
          {onboardingData.length > 0 ? (
            <Swiper
              ref={sliderRef}
              slidesPerView={1}
              spaceBetween={20}
              pagination={{
                clickable: true,
              }}
              modules={[Navigation, Pagination]}
              onSlideChange={(e) => onSwiperMove(e)}
              freeMode={false}
            >
              {onboardingData.map((item, index) => (
                <SwiperSlide
                  key={index}
                  data-id={index}
                  style={{ textAlign: "center" }}
                >
                  <div
                    className="image-onboarding"
                    style={{ marginBottom: isMobileVersion ? 18 : 32 }}
                  >
                    <Image
                      style={{
                        objectFit: "cover",
                        borderRadius: isMobileVersion ? 8 : 0,
                      }}
                      width={isMobileVersion ? "100%" : 416}
                      height={isMobileVersion ? 176 : 230}
                      preview={false}
                      src={item.image}
                      fallback={defaultImage}
                    />
                  </div>
                  <div
                    className="title-onboarding"
                    style={{
                      marginBottom: isMobileVersion ? 12 : 10,
                      fontSize: isMobileVersion ? 16 : 22,
                      fontWeight: 500,
                      color: isMobileVersion
                        ? colorTextTitleMobile
                        : colorTextTitle,
                      lineHeight: isMobileVersion ? "19px" : "normal",
                    }}
                  >
                    {item.title}
                  </div>
                  <div
                    className="desc-onboarding"
                    style={{
                      fontSize: 14,
                      color: colorTextDesc,
                      marginBottom: isMobileVersion ? 18 : 31,
                    }}
                  >
                    {item.desc}
                  </div>
                  <div
                    style={{
                      display: index < 2 ? "none" : "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      onClick={() => {
                        setIsOpen(false);
                        dispatch(setCanFetchNotifProgram(true));
                      }}
                      role="button"
                      className="btn-primary"
                      style={{
                        width: isMobileVersion ? "100%" : 227,
                        borderRadius: 6,
                        border: "none",
                        fontWeight: "500",
                        height: isMobileVersion ? 40 : 48,
                        fontSize: "14px",
                        lineHeight: isMobileVersion ? "16px" : "normal",
                      }}
                    >
                      {t("feature.onboarding.call_to_action")}
                    </Button>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : null}
          <div
            style={{
              display: "flex",
              position: "absolute",
              top: "25%",
              width: "100%",
              left: 0,
              justifyContent: "space-between",
            }}
          >
            {!isMobileVersion && (
              <div
                onClick={handlePrev}
                style={{
                  color: ColorPrimary,
                  background: ColorSecondary,
                  borderRadius: "8px",
                  border: "1px solid " + ColorPrimary,
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  justifyContent: "center",
                  zIndex: 5,
                  cursor: "pointer",
                  marginLeft: 17,
                }}
                id="id-btn-prev-onboarding"
                className="swiper-button btn-prev-onboarding hidden"
                name="btn-swiper-prev-onboarding"
              >
                <LeftOutlined />
              </div>
            )}
            {!isMobileVersion && (
              <div
                onClick={handleNext}
                style={{
                  color: ColorPrimary,
                  background: ColorSecondary,
                  borderRadius: "8px",
                  border: "1px solid " + ColorPrimary,
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  justifyContent: "center",
                  zIndex: 5,
                  cursor: "pointer",
                  marginRight: 17,
                }}
                id="id-btn-next-onboarding"
                className="swiper-button btn-next-onboarding"
                name="btn-swiper-next-onboarding"
              >
                <RightOutlined />
              </div>
            )}
          </div>
        </div>
      </Modal>
    </ConfigProvider>
  );
};

export default ModalOnboarding;
